// Ползунки баланса. Движение ползунка шлёт POST /simulate и перерисовывает
// графики: тысяча seed считается за 0.08 с, поэтому задержки в 300 мс
// достаточно, чтобы не слать запрос на каждый пиксель протяжки.
//
// ГРАНИЦЫ ПОЛЗУНКОВ — НЕ ВКУСОВЩИНА. Они повторяют проверки LevelBudget
// и PopulationBudget: `ammoBudgetPermille` обязан лежать в [0, 999], иначе
// генератор бросит «патронов не может быть столько же, сколько нужно»
// (TR-CMB-05). Ползунок, который умеет поставить заведомо невыполнимое
// число, — это ползунок, который умеет уронить прогон.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { BalanceConfig, Preset } from '../lib/types';
import { num } from '../lib/format';
import { Panel } from './Ui';

export interface SliderSpec {
  key: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  get: (config: BalanceConfig) => number;
  set: (config: BalanceConfig, value: number) => BalanceConfig;
  /**
   * Применим ли ползунок к этому пресету. Ползунок, которому нечего
   * крутить, показывается выключенным с объяснением — а не прячется:
   * исчезнувшая настройка читается как «такой настройки нет», хотя она
   * есть, просто не у этого пресета.
   */
  available?: (config: BalanceConfig) => boolean;
  /** Почему выключен. Показывается вместо подсказки. */
  unavailableHint?: string;
}

const clone = (config: BalanceConfig): BalanceConfig => ({
  ...config,
  sizes: { ...config.sizes },
  population: { ...config.population },
  ...(config.drops
    ? { drops: { ...config.drops, entries: config.drops.entries.map((entry) => ({ ...entry })) } }
    : {}),
});

const pop = (
  key: keyof BalanceConfig['population'],
  label: string,
  hint: string,
  min: number,
  max: number,
  step = 1,
): SliderSpec => ({
  key,
  label,
  hint,
  min,
  max,
  step,
  get: (config) => config.population[key],
  set: (config, value) => {
    const next = clone(config);
    next.population[key] = value;
    return next;
  },
});

const top = (key: 'minRooms' | 'maxRooms' | 'gridWidth' | 'gridHeight' | 'maxFloorCells',
  label: string, hint: string, min: number, max: number, step = 1): SliderSpec => ({
  key,
  label,
  hint,
  min,
  max,
  step,
  get: (config) => config[key],
  set: (config, value) => {
    const next = clone(config);
    next[key] = value;
    return next;
  },
});

/**
 * Шанс дропа с врага.
 *
 * Стоит первым не из вежливости: это единственный ползунок, который
 * умеет сломать первый столп игры, не тронув ни одного числа населения.
 * Патроны с трупов — тоже патроны на уровне, и на 40% дефицит перестаёт
 * держаться на 41.2% уровней (замер на 1000 прогонов, CONTRACT.md §1).
 */
const dropChance: SliderSpec = {
  key: 'dropChancePercent',
  label: 'Шанс дропа с врага, %',
  hint: 'вероятность, что с трупа упадёт ровно один предмет по весам таблицы',
  min: 0,
  max: 100,
  step: 1,
  get: (config) => config.drops?.chancePercent ?? 0,
  set: (config, value) => {
    const next = clone(config);
    if (next.drops) next.drops.chancePercent = value;
    return next;
  },
  available: (config) => config.drops !== undefined,
  unavailableHint:
    'у этого пресета нет секции drops — шанс не к чему применять. Возьмите пресет с таблицей дропа.',
};

export const SLIDERS: readonly SliderSpec[] = [
  dropChance,
  pop('enemiesPerHundredCells', 'Плотность врагов', 'врагов на сто клеток пола', 0, 30),
  pop('minEnemies', 'Врагов минимум', 'нижняя отсечка населения', 0, 30),
  pop('maxEnemies', 'Врагов максимум', 'верхняя отсечка населения', 0, 40),
  pop('averageShotsPerEnemy', 'Выстрелов на врага', 'сколько патронов уходит на одного', 1, 12),
  pop('ammoBudgetPermille', 'Бюджет патронов, ‰', 'доля от нужного жадному; 999 — потолок правила дефицита', 0, 999, 10),
  pop('ammoStackMin', 'Пачка патронов, мин', 'сколько патронов в самой маленькой находке', 1, 24),
  pop('ammoStackMax', 'Пачка патронов, макс', 'сколько патронов в самой крупной находке', 1, 48),
  pop('minMedkits', 'Аптечек минимум', 'нижняя граница лечения на уровне', 0, 8),
  pop('maxMedkits', 'Аптечек максимум', 'верхняя граница лечения на уровне', 0, 12),
  top('minRooms', 'Комнат минимум', 'вход, сейв-комната и выход — это уже три', 3, 24),
  top('maxRooms', 'Комнат максимум', 'потолок числа комнат уровня', 3, 32),
  top('gridWidth', 'Сетка, ширина', 'клеток по X; комната обязана влезть', 8, 64),
  top('gridHeight', 'Сетка, высота', 'клеток по Y', 8, 64),
  top('maxFloorCells', 'Площадь пола, макс', 'потолок суммы клеток всех комнат', 20, 400, 5),
];

/** Проверки того же смысла, что и в LevelBudget/PopulationBudget. */
export function validateConfig(config: BalanceConfig): string[] {
  const errors: string[] = [];
  const p = config.population;
  if (config.minRooms < 3) errors.push('MinRooms: уровню нужны как минимум вход, сейв-комната и выход.');
  if (config.maxRooms < config.minRooms) errors.push('MaxRooms меньше MinRooms.');
  if (p.maxEnemies < p.minEnemies) errors.push('MaxEnemies меньше MinEnemies.');
  if (p.averageShotsPerEnemy < 1) errors.push('AverageShotsPerEnemy: врага нельзя убить нулём патронов.');
  if (p.ammoBudgetPermille > 999)
    errors.push('AmmoBudgetPermille вне [0, 999]: патронов не может быть столько же, сколько нужно.');
  if (p.ammoStackMax < p.ammoStackMin) errors.push('AmmoStackMax меньше AmmoStackMin.');
  if (p.maxMedkits < p.minMedkits) errors.push('MaxMedkits меньше MinMedkits.');
  const maxSide = Math.max(config.sizes.largeWidth, config.sizes.largeHeight);
  if (maxSide > config.gridWidth || maxSide > config.gridHeight)
    errors.push(`Комната стороной ${maxSide} не влезает в сетку ${config.gridWidth}×${config.gridHeight}.`);
  if (config.drops) {
    const chance = config.drops.chancePercent;
    if (!Number.isFinite(chance) || chance < 0 || chance > 100)
      errors.push('Шанс дропа вне [0, 100]: вероятность не бывает отрицательной и больше единицы.');
    // Таблица из одних нулевых весов — это не «ничего не падает», а
    // деление на ноль при розыгрыше. Симуляции такое отдавать нельзя.
    const weight = config.drops.entries.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0);
    if (chance > 0 && weight <= 0)
      errors.push('Шанс дропа больше нуля, а суммарный вес таблицы равен нулю: разыгрывать нечего.');
  }
  return errors;
}

export interface SliderPanelProps {
  presets: Preset[];
  runs: number;
  seedFrom: number;
  onRunsChange: (value: number) => void;
  onSeedFromChange: (value: number) => void;
  onChange: (config: BalanceConfig) => void;
  onReset: () => void;
  serverOnline: boolean;
  pending: boolean;
  error: string | null;
  elapsedMs: number | null;
  /** Сколько сервер потратил на сам прогон, без сети. */
  serverMs: number | null;
  cacheHit: boolean;
  simulated: boolean;
}

export function SliderPanel(props: SliderPanelProps) {
  const { presets, onChange } = props;
  const [presetFile, setPresetFile] = useState<string | null>(null);
  const [config, setConfig] = useState<BalanceConfig | null>(null);
  const baseline = useRef<BalanceConfig | null>(null);

  useEffect(() => {
    if (presets.length === 0 || config !== null) return;
    const first = presets[0] as Preset;
    setPresetFile(first.file);
    setConfig(clone(first.config));
    baseline.current = clone(first.config);
  }, [presets, config]);

  const errors = useMemo(() => (config ? validateConfig(config) : []), [config]);

  if (config === null) {
    return (
      <Panel title="Ползунки баланса">
        <div className="empty">пресеты не загружены</div>
      </Panel>
    );
  }

  const apply = (next: BalanceConfig) => {
    setConfig(next);
    if (validateConfig(next).length === 0) onChange(next);
  };

  const pickPreset = (file: string) => {
    const preset = presets.find((p) => p.file === file);
    if (!preset) return;
    setPresetFile(file);
    const fresh = clone(preset.config);
    baseline.current = clone(preset.config);
    setConfig(fresh);
    props.onReset();
  };

  const changed = baseline.current
    ? SLIDERS.filter((s) => s.get(config) !== s.get(baseline.current as BalanceConfig))
    : [];

  return (
    <Panel
      title="Ползунки баланса"
      aside={
        <div className="controls">
          <label className="field">
            пресет
            <select value={presetFile ?? ''} onChange={(e) => pickPreset(e.target.value)}>
              {presets.map((preset) => (
                <option key={preset.file} value={preset.file}>
                  {preset.config.name} · {preset.file}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            seed от
            <input
              type="number"
              value={props.seedFrom}
              min={1}
              style={{ width: '7ch' }}
              onChange={(e) => props.onSeedFromChange(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <label className="field">
            прогонов
            <input
              type="number"
              value={props.runs}
              min={1}
              max={100000}
              style={{ width: '8ch' }}
              onChange={(e) => props.onRunsChange(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <button
            className="action"
            disabled={changed.length === 0 && !props.simulated}
            onClick={() => {
              if (baseline.current) setConfig(clone(baseline.current));
              props.onReset();
            }}
          >
            вернуть пресет
          </button>
        </div>
      }
    >
      {!props.serverOnline && (
        <div className="callout hard">
          <h3>Пересчёт недоступен: сервер на 127.0.0.1:5177 не отвечает</h3>
          <p>
            Ползунки двигаются и показывают, каким станет конфиг, но графики останутся на выбранном
            датасете. Пересчитать выборку без настоящей симуляции нельзя, а нарисовать вместо неё
            правдоподобную картинку — худшее, что может сделать балансовый инструмент: ровно от этого
            предостерегает ADR-0023. Поднимите сервер или прогоните конфиг руками командой ниже.
          </p>
        </div>
      )}

      {errors.length > 0 && (
        <div className="callout hard">
          <h3>Конфиг невыполним — запрос не уходит</h3>
          {errors.map((error) => (
            <p key={error} className="mono">
              {error}
            </p>
          ))}
        </div>
      )}

      {props.error && (
        <div className="callout hard">
          <h3>Сервер отказал</h3>
          <p className="mono">{props.error}</p>
        </div>
      )}

      <div className="sliders">
        {SLIDERS.map((spec) => {
          const value = spec.get(config);
          const base = baseline.current ? spec.get(baseline.current) : value;
          const usable = spec.available === undefined || spec.available(config);
          return (
            <div className={usable ? 'slider' : 'slider off'} key={spec.key}>
              <span className="title">{spec.label}</span>
              <span className={value === base ? 'readout' : 'readout changed'}>
                {usable ? num(value) : '—'}
                {usable && value !== base && <span className="faint"> ← {num(base)}</span>}
              </span>
              <input
                type="range"
                min={spec.min}
                max={spec.max}
                step={spec.step}
                value={value}
                disabled={!usable}
                onChange={(e) => apply(spec.set(config, Number(e.target.value)))}
              />
              <span className={usable ? 'hint' : 'hint warn'}>
                {usable ? spec.hint : (spec.unavailableHint ?? spec.hint)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="controls" style={{ marginTop: 12 }}>
        <span className="dim">
          {props.pending
            ? 'считаем…'
            : props.simulated
              ? `пересчитано за ${num(props.elapsedMs ?? 0)} мс` +
                (props.serverMs !== null ? ` (симуляция ${num(props.serverMs)} мс)` : '') +
                (props.cacheHit ? ' · из кэша сервера' : '')
              : changed.length > 0
                ? `изменено настроек: ${changed.length}`
                : 'настройки как в пресете'}
        </span>
      </div>
    </Panel>
  );
}

/** Готовая команда прогона текущего конфига мимо приложения. */
export function cliCommand(config: BalanceConfig, runs: number, seedFrom: number): string {
  return (
    `# положите конфиг в файл и прогоните:\n` +
    `Tools/SimCli/bin/Debug/net7.0/spusk-sim balance --runs ${runs} --seed-from ${seedFrom} ` +
    `--config config.json --out runs.jsonl\n\n` +
    JSON.stringify(config, null, 2)
  );
}
